import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCatalog } from "../data/useCatalog";
import CategoryModal from "./CategoryModal";

const MOVE_MS = 460;
const ROWS = 2;
const SEARCH_LIMIT = 8;

const toColumns = (items) => {
  const columns = [];
  for (let index = 0; index < items.length; index += ROWS) {
    columns.push(items.slice(index, index + ROWS));
  }
  return columns;
};

const ProductsSection = () => {
  const { t, i18n } = useTranslation();
  const catalog = useCatalog();
  const initialColumnCount = Math.ceil(catalog.length / ROWS);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [highlightProductId, setHighlightProductId] = useState(null);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeResult, setActiveResult] = useState(0);
  const searchRef = useRef(null);
  const [index, setIndex] = useState(initialColumnCount);
  const [instant, setInstant] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(4);
  const lockRef = useRef(false);
  const columns = useMemo(() => toColumns(catalog), [catalog]);
  const columnCount = columns.length;
  const track = useMemo(
    () => [...columns, ...columns, ...columns],
    [columns]
  );
  const selectedCategory =
    catalog.find((category) => category.id === selectedCategoryId) || null;
  const searchTerm = query.trim().toLocaleLowerCase(i18n.language);
  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    const matches = [];
    catalog.forEach((category) => {
      category.products.forEach((product) => {
        if (
          product.name.toLocaleLowerCase(i18n.language).includes(searchTerm)
        ) {
          matches.push({ category, product });
        }
      });
    });
    return matches.slice(0, SEARCH_LIMIT);
  }, [catalog, i18n.language, searchTerm]);

  useEffect(() => {
    setActiveResult(0);
  }, [searchTerm]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!searchRef.current?.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [searchOpen]);

  const openProduct = (categoryId, productId) => {
    setHighlightProductId(productId);
    setSelectedCategoryId(categoryId);
    setQuery("");
    setSearchOpen(false);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      setSearchOpen(false);
      return;
    }
    if (!searchOpen || searchResults.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveResult((current) => (current + 1) % searchResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveResult((current) =>
        (current - 1 + searchResults.length) % searchResults.length
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const match = searchResults[activeResult];
      if (match) openProduct(match.category.id, match.product.id);
    }
  };

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const apply = () => setVisibleColumns(media.matches ? 2 : 4);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!instant) return undefined;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setInstant(false));
    });
    return () => cancelAnimationFrame(frame);
  }, [instant]);

  useEffect(() => {
    if (columnCount === 0) return undefined;
    if (index >= columnCount && index < columnCount * 2) return undefined;
    const timeout = window.setTimeout(() => {
      setInstant(true);
      setIndex((current) =>
        current >= columnCount * 2 ? current - columnCount : current + columnCount
      );
    }, MOVE_MS);
    return () => window.clearTimeout(timeout);
  }, [index, columnCount]);

  const unlockLater = () => {
    window.setTimeout(() => {
      lockRef.current = false;
    }, MOVE_MS + 40);
  };

  const scrollBy = (delta) => {
    if (lockRef.current || columnCount === 0) return;
    lockRef.current = true;
    setInstant(false);
    setIndex((current) => current + delta);
    unlockLater();
  };

  return (
    <section id="products" className="section products-section">
      <h2>{t("products.title")}</h2>
      <div className="products-search" ref={searchRef}>
        <input
          id="product-search"
          type="search"
          value={query}
          placeholder={t("products.searchPlaceholder")}
          aria-label={t("products.search")}
          autoComplete="off"
          role="combobox"
          aria-expanded={searchOpen && searchTerm.length > 0}
          aria-controls="product-search-results"
          aria-activedescendant={
            searchOpen && searchResults[activeResult]
              ? `product-search-${searchResults[activeResult].category.id}-${searchResults[activeResult].product.id}`
              : undefined
          }
          onChange={(event) => {
            setQuery(event.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={handleSearchKeyDown}
        />
        {searchOpen && searchTerm ? (
          <ul id="product-search-results" className="products-search-results" role="listbox">
            {searchResults.length === 0 ? (
              <li className="products-search-empty">{t("products.noResults")}</li>
            ) : (
              searchResults.map((match, resultIndex) => (
                <li
                  key={`${match.category.id}-${match.product.id}`}
                  role="presentation"
                >
                  <button
                    type="button"
                    role="option"
                    id={`product-search-${match.category.id}-${match.product.id}`}
                    aria-selected={resultIndex === activeResult}
                    className={resultIndex === activeResult ? "is-active" : ""}
                    onMouseEnter={() => setActiveResult(resultIndex)}
                    onClick={() => openProduct(match.category.id, match.product.id)}
                  >
                    <span className="products-search-product">{match.product.name}</span>
                    <span className="products-search-category">{match.category.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      <div
        className="products-scroller"
        style={{ "--products-columns": String(visibleColumns) }}
      >
        <button
          type="button"
          className="products-arrow products-arrow-prev"
          aria-label={t("products.previous")}
          onClick={() => scrollBy(-1)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M14.5 5.5 8 12l6.5 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="products-viewport">
          <div
            className={`products-track${instant ? " is-instant" : ""}`}
            style={{
              transform: `translateX(calc(${(i18n.dir() === "rtl" ? index : -index)} * (100% + var(--products-gap)) / var(--products-columns)))`,
            }}
          >
            {track.map((column, columnIndex) => {
              const inView =
                columnIndex >= index && columnIndex < index + visibleColumns;
              return (
                <div
                  className="products-column"
                  key={`${column[0]?.id || "empty"}-${columnIndex}`}
                  inert={inView ? undefined : true}
                >
                  {column.map((category) => (
                    <button
                      type="button"
                      key={category.id}
                      className="category-card"
                      onClick={() => {
                        setHighlightProductId(null);
                        setSelectedCategoryId(category.id);
                      }}
                    >
                      <div className="category-image-wrapper">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="category-image"
                          loading="lazy"
                        />
                      </div>
                      <div className="category-name">
                        <span>{category.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          className="products-arrow products-arrow-next"
          aria-label={t("products.next")}
          onClick={() => scrollBy(1)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M14.5 5.5 8 12l6.5 6.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {selectedCategory && (
        <CategoryModal
          category={selectedCategory}
          highlightProductId={highlightProductId}
          onClose={() => {
            setSelectedCategoryId(null);
            setHighlightProductId(null);
          }}
        />
      )}
    </section>
  );
};

export default ProductsSection;
